/**
 * @jsxFrag
 */
import React, { useCallback, useContext, useMemo, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import type { DocNode } from '@atlaskit/adf-schema';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { cssMap } from '@atlaskit/css';
import { MenuGroup, Section } from '@atlaskit/menu';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import { usePublish } from '@atlaskit/rovo-triggers';
import { token } from '@atlaskit/tokens';
import {
	type AIAction,
	useAIActions,
	useAIAutomationsConfig,
	useAIContext,
	useAIProfileData,
	useAISurfaceMetadata,
	useViewAIActions,
} from '@atlassian/ai-smart-button-api';
import { useCreateAgentAtlasURL } from '@atlassian/conversation-assistant-agent';

// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { AIDynamicSurfaceContext } from '../ai-dynamic-surface/AIDynamicSurfaceContext';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { DYNAMIC_SURFACE_PANEL_ID } from '../ai-dynamic-surface/useDynamicSurfacePanel';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import type { ObjectSidebar } from '../common/types/object-sidebar-types';

import { DynamicSurfaceActionItem } from './components/action-item';
import { AIInput } from './components/ai-input';
import { FeedbackActionItem } from './components/feedback-item';
import { HeadingSection } from './components/heading-section';
import { NoResults } from './components/no-results';
import { useNavigateActions } from './hooks/useNavigateActions';
import { getStudioActions, type StudioAction } from './utils/get-studio-actions';

export const i18n = defineMessages({
	aiActionsSectionText: {
		id: 'ai-smart-button.ai-actions.actions.section.text',
		defaultMessage: 'Actions',
		description: 'The section text for the AI Actions in a Menu Group',
	},
	aiStudioSectionText: {
		id: 'ai-smart-button.ai-actions.rovo-agents.section.text',
		defaultMessage: 'Studio',
		description:
			'The section text for the AI Studio section (containing rovo agents) in a Menu Group',
	},
	aiHelpSectionText: {
		id: 'ai-smart-button.ai-actions.help.section.text',
		defaultMessage: 'Help',
		description: 'The section text for the Help Section in a Menu Group',
	},
	giveFeedbackActionTitle: {
		id: 'ai-smart-button.ai-actions.give-feedback-action.title',
		defaultMessage: 'Give feedback',
		description: 'The title for the give feedback action in the help section',
	},
});

const styles = cssMap({
	container: {
		height: '100%',
	},
	sectionHeader: {
		color: token('color.text.subtlest'),
		fontWeight: token('font.weight.medium'),
		paddingInline: token('space.300'),
		paddingTop: token('space.150'),
		paddingBottom: token('space.050'),
	},
	scrollContainer: {
		overflow: 'auto',
	},
});

const SectionHeader = ({ title }: { title: string }) => {
	return <Box xcss={styles.sectionHeader}>{title}</Box>;
};

type AIActionsProps = {
	name: string;
	objectSidebar: ObjectSidebar;
};

export const AIActions = ({ name, objectSidebar }: AIActionsProps) => {
	const intl = useIntl();
	const { changeView } = useViewAIActions();
	const surfaceMetadata = useAISurfaceMetadata();
	const profileData = useAIProfileData();
	const automationsConfig = useAIAutomationsConfig();
	const actions = useAIActions();
	const context = useAIContext();
	const { activeAction, setActiveAction } = useContext(AIDynamicSurfaceContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const showFeedbackAction = fg('smart_ai_button_feedback_action');

	/**
	 * If active action has children that render all children actions.
	 * AIDynamicSurface component will take care of showing back button in panel header.
	 */
	const isActionWithChildren = !!activeAction?.children;
	const selectedActions = !!activeAction?.children ? activeAction.children : actions;
	/**
	 * We want to hide the main AI actions for databases since they currently do not support AI features yet
	 */
	const isMainAiActionsShown = surfaceMetadata.contentType !== 'database';
	const helpActions = useMemo(
		() =>
			showFeedbackAction
				? [
						{
							component: (
								<FeedbackActionItem surfaceMetadata={surfaceMetadata} profileData={profileData} />
							),
							title: intl.formatMessage(i18n.giveFeedbackActionTitle),
						},
					]
				: [],
		[surfaceMetadata, showFeedbackAction, profileData, intl],
	);

	const [input, setInput] = useState('');

	const handleAction = useCallback(
		(action: AIAction | StudioAction) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: surfaceMetadata.contentType,
					action: 'clicked',
					actionSubject: 'rovoButton',
					actionSubjectId: 'aiRovoDynamicSurfaceMenuItem',
					attributes: {
						pageMode: surfaceMetadata.pageMode,
						id: action.id,
						itemType: 'action',
					},
				},
			}).fire();
			//if the user has typed something in the input field to filter menu actions, we want to fire an additional analytics event
			if (input.length) {
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						source: surfaceMetadata.contentType,
						action: 'clicked',
						actionSubject: 'rovoButton',
						actionSubjectId: 'aiRovoDynamicSurfaceChatFilteredMenuItem',
						attributes: {
							product: surfaceMetadata.product,
							productPage: surfaceMetadata.contentType,
							pageMode: surfaceMetadata.pageMode,
							id: action.id,
							itemType: 'action',
						},
					},
				}).fire();
			}
			/**
			 * If the action has children, set the active action so that
			 * we can display the children action
			 */
			if ('children' in action) {
				setActiveAction(action);
			}

			if ('viewConfig' in action) {
				changeView(action);
				return;
			}
			if (action.onClick) {
				action.onClick();
			}
			if ('closePanelOnSelection' in action) {
				objectSidebar.actions.hideObjectSidebarByPanelId(DYNAMIC_SURFACE_PANEL_ID);
			}
		},
		[
			changeView,
			objectSidebar.actions,
			setActiveAction,
			createAnalyticsEvent,
			surfaceMetadata,
			input,
		],
	);

	const handleInput = useCallback((text: string) => {
		setInput(text);
	}, []);

	const filteredAIActions = useMemo(
		() =>
			selectedActions.filter(({ title }) => {
				return title.toLowerCase().includes(input.toLowerCase());
			}),
		[selectedActions, input],
	);

	const publish = usePublish('ai-mate');

	const createAgentUrl = useCreateAgentAtlasURL(surfaceMetadata?.siteId || '');
	const handleBrowseAgent = React.useCallback(() => {
		if (!surfaceMetadata) {
			return;
		}
		publish({
			type: 'open-browse-agent-sidebar',
			product: surfaceMetadata.product,
			source: 'ai-smart-button-dynamic-surface',
		});
	}, [publish, surfaceMetadata]);

	const studioActions = useMemo(() => {
		/**
		 * Don't show studio section when;
		 * - children action of active actions is shown.
		 * - when there is editor selection.
		 * Need to create separate studio actions and pass them along with
		 * 	consumption and editing actions.
		 * So that don't need to handle separate logic for studio actions.
		 */
		if (context?.reference || isActionWithChildren || !surfaceMetadata) {
			return [];
		}
		return getStudioActions({
			intl,
			createAgentUrl,
			handleBrowseAgent,
			renderUserAutomations: automationsConfig?.userAutomations?.render,
			onCreateAutomation: automationsConfig?.adminAutomations?.onClick,
		});
	}, [
		intl,
		createAgentUrl,
		handleBrowseAgent,
		context?.reference,
		isActionWithChildren,
		surfaceMetadata,
		automationsConfig?.userAutomations?.render,
		automationsConfig?.adminAutomations?.onClick,
	]);

	const filteredStudioActions = useMemo(
		() =>
			studioActions.filter(({ title }) => {
				return title.toLowerCase().includes(input.toLowerCase());
			}),
		[input, studioActions],
	);

	const filteredHelpActions = useMemo(
		() =>
			helpActions.filter(({ title }) => {
				return title.toLowerCase().includes(input.toLowerCase());
			}),
		[input, helpActions],
	);

	const filteredFullList = useMemo(
		() => ([] as (AIAction | StudioAction)[]).concat(filteredAIActions, filteredStudioActions),
		[filteredAIActions, filteredStudioActions],
	);

	const filteredFullListValues = useMemo(
		() => filteredFullList.map(({ id }) => id),
		[filteredFullList],
	);

	const { handleArrowDown, handleArrowUp, selectedIndex } = useNavigateActions(
		filteredFullListValues.length,
	);

	const handleSelect = useCallback(
		(adf: DocNode) => {
			if (filteredFullList.length && selectedIndex !== undefined) {
				const filteredAction = filteredFullList?.[selectedIndex];
				if (filteredAction) {
					handleAction(filteredAction);
				}
			} else if (input.length) {
				publish({
					type: 'chat-new',
					product: surfaceMetadata?.product,
					source: 'ai-smart-button-dynamic-surface',
					data: {
						name: input,
						dialogues: [],
						prompt: adf,
					},
				});
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						source: surfaceMetadata.contentType,
						action: 'invoked',
						actionSubject: 'rovoButton',
						actionSubjectId: 'aiRovoDynamicSurfaceChatInput',
						attributes: {
							pageMode: surfaceMetadata.pageMode,
						},
					},
				}).fire();
			}
		},
		[
			filteredFullList,
			selectedIndex,
			handleAction,
			input,
			publish,
			surfaceMetadata,
			createAnalyticsEvent,
		],
	);

	const handleBack = useCallback(() => {
		if (!activeAction) {
			return;
		}
		setActiveAction(undefined);
	}, [setActiveAction, activeAction]);

	return (
		<Stack grow="fill" xcss={styles.container}>
			{filteredFullList.length || filteredHelpActions.length ? (
				<>
					<Stack grow="fill" xcss={styles.scrollContainer}>
						<MenuGroup>
							{!isActionWithChildren && <HeadingSection name={name} />}
							{filteredAIActions.length && isMainAiActionsShown ? (
								<Section>
									<SectionHeader
										title={
											isActionWithChildren
												? activeAction.title
												: intl.formatMessage(i18n.aiActionsSectionText)
										}
									/>
									{filteredAIActions.map((action, index) => (
										<DynamicSurfaceActionItem
											key={action.id}
											onClick={() => handleAction(action)}
											title={action.title}
											icon={action.icon}
											isSelected={selectedIndex === index}
											status={action.status}
										/>
									))}
								</Section>
							) : null}
							{filteredStudioActions.length ? (
								<Section>
									<SectionHeader title={intl.formatMessage(i18n.aiStudioSectionText)} />
									{filteredStudioActions.map((action, index) => (
										<DynamicSurfaceActionItem
											key={action.id}
											title={action.title}
											icon={action.icon}
											// Account for the AI Actions list on top as well
											isSelected={selectedIndex === filteredAIActions.length + index}
											{...('href' in action && action?.href
												? {
														href: action.href,
														onClick: () => handleAction(action),
													}
												: {
														onClick: () => handleAction(action),
													})}
										/>
									))}
								</Section>
							) : null}
							{filteredHelpActions.length ? (
								<Section>
									<SectionHeader title={intl.formatMessage(i18n.aiHelpSectionText)} />
									{filteredHelpActions.map(({ component }, index) => (
										<React.Fragment key={index}>{component}</React.Fragment>
									))}
								</Section>
							) : null}
						</MenuGroup>
					</Stack>
				</>
			) : (
				<Stack alignInline="center" grow="fill">
					<NoResults />
				</Stack>
			)}
			<AIInput
				surfaceMetadata={surfaceMetadata}
				onSelect={handleSelect}
				onInputChange={handleInput}
				handleArrowDown={handleArrowDown}
				handleArrowUp={handleArrowUp}
				handleBack={handleBack}
			/>
		</Stack>
	);
};
